import type {
  AccidentData,
  AccidentDataFormData,
  EnvironmentalData,
  EnvironmentalDataFormData,
  FormularAB,
  FormularABFormData,
  FormularC,
  FormularCFormData,
  FormularD,
  FormularDFormData,
  InputMaterialEntry,
  PrimaryFuelEntry,
  RotaryKiln,
} from '../../types/reports.js';
import { PRIMARY_FUEL_CATEGORIES, PRIMARY_FUEL_TYPES } from './environmental/BrennstoffeSection.js';
import { MATERIAL_KEYS } from './environmental/EinsatzstoffeSection.js';
import {
  CALCULATION_BASIS_CONCENTRATION,
  createEmptyLoadCalculation,
  type KilnLoadCalculationBasis,
} from './environmental/kilnFields.js';
import { FORMULAR_D_FIELD_KEYS } from './FormularDForm.js';

/**
 * Hydrierungs-Funktionen fuer alle fuenf Report-Typen (D-04): aus ReportEditPage.tsx
 * herausgezogen, damit sie nicht mehr die Katalog-Konstanten (PRIMARY_FUEL_CATEGORIES,
 * PRIMARY_FUEL_TYPES, MATERIAL_KEYS) aufwaerts aus den tiefen Section-Komponenten in die
 * Page importieren muessen.
 *
 * Die zwei verbleibenden Array-Hydrierer sind nur modulintern gebraucht (nicht exportiert):
 * sie hydrieren ein sparse Array aus der DB zu einem dichten, index-stabilen Array fester
 * Laenge (Katalog-Reihenfolge), Voraussetzung fuer das feste Index-Binding
 * (z.B. primaryFuels.N.*) aus Phase 17. Sekundaerbrennstoffe (`alternativeFuels`) sind seit
 * Phase 34 eine freie Liste ohne Katalogreihenfolge und werden weiter unten nur noch
 * durchgereicht, nicht mehr ueber einen Hydrierer aufgefuellt.
 */

// Hydriert ein sparse Einsatzstoff-Array (aus der DB, ggf. mit fehlenden Materialien) zu einem
// dichten, index-stabilen Array fester Laenge (Katalog-Reihenfolge). Dies ermoeglicht das
// Mantine-Form-Binding ueber feste Indizes (inputMaterials.N.*) statt sparse find/push.
function hydrateInputMaterials(sparse?: InputMaterialEntry[] | null): InputMaterialEntry[] {
  return MATERIAL_KEYS.map(
    (key) =>
      sparse?.find((m) => m.materialType === key) ?? {
        materialType: key,
        clinkerProduction: null,
        cementMilling: null,
      },
  );
}

// Hydriert das sparse primaryFuels-Array (aus der DB) zu einem dichten, index-stabilen Array
// fester Laenge (28 = 4 Kategorien x 7 Typen, Kategorie aussen/Typ innen). Reihenfolge MUSS exakt
// zur PRIMARY_FUEL_INDEX-Berechnung in BrennstoffeSection.tsx passen.
function hydratePrimaryFuels(sparse?: PrimaryFuelEntry[] | null): PrimaryFuelEntry[] {
  const result: PrimaryFuelEntry[] = [];
  for (const category of PRIMARY_FUEL_CATEGORIES) {
    for (const fuel of PRIMARY_FUEL_TYPES) {
      result.push(
        sparse?.find((f) => f.category === category.key && f.fuelType === fuel.key) ?? {
          fuelType: fuel.key,
          category: category.key,
          amount: null,
          heatingValue: null,
          emissionFactor: null,
          ashContent: null,
        },
      );
    }
  }
  return result;
}

// Hydriert das rotaryKilns-Array je Ofen (D-10): `measurements` wird auf ein Array normalisiert
// (?? []), damit form.insertListItem nie auf null operiert -- derselbe Fehlermodus, der in 17-04
// bei den Sonstige-Brennstoffen schon einmal zu einem still fehlschlagenden Hinzufuegen fuehrte.
//
// 19-10-Gap-Fix (D-08, siehe 19-08-SUMMARY.md GAP 2): das Backend liefert `measurementDate`
// (eine DATE_IMMUTABLE-Spalte) als vollen ISO-Timestamp ("2026-01-01T00:00:00+01:00"), NICHT als
// "YYYY-MM-DD". Jede gesetzte measurementDate wird deshalb beim Laden auf die ersten 10 Zeichen
// gekuerzt, sodass im Formularbaum durchgaengig "YYYY-MM-DD" vorliegt -- genau das Format, das
// DateInput, die Sortierung unten UND `formatMeasurementDateForTitle`
// (KilnMeasurementCard.tsx) erwarten. `null`/leer bleibt unveraendert.
//
// Zusaetzlich werden die Messungen je Ofen aufsteigend nach measurementDate sortiert, undatierte
// Messungen ans Ende (stabil nach id) -- das ist bewusst die EINZIGE Stelle, an der sortiert
// wird: D-10 verlangt, dass die Umsortierung erst beim Neuladen greift, damit ein nachgetragenes
// Datum den Eintrag nicht waehrend der Bearbeitung unter dem Cursor wegspringen laesst. Eine
// Sortierung im Rendern der Komponente (DrehofenSection) waere also ein Fehler, kein Fortschritt.
// Die Oefen selbst bleiben in der vom Backend gelieferten Reihenfolge (dort bereits per
// #[ORM\OrderBy] auf sortOrder sortiert).
function hydrateRotaryKilns(kilns?: RotaryKiln[] | null): RotaryKiln[] {
  return (kilns ?? []).map((kiln) => {
    const normalizedMeasurements = (kiln.measurements ?? []).map((measurement) => ({
      ...measurement,
      measurementDate: measurement.measurementDate
        ? measurement.measurementDate.slice(0, 10)
        : measurement.measurementDate,
    }));
    const measurements = [...normalizedMeasurements].sort((a, b) => {
      // measurementDate ist nach der Normalisierung oben ein ISO-String "YYYY-MM-DD" --
      // lexikographischer Vergleich genuegt.
      if (!a.measurementDate && !b.measurementDate) return (a.id ?? 0) - (b.id ?? 0);
      if (!a.measurementDate) return 1;
      if (!b.measurementDate) return -1;
      return a.measurementDate < b.measurementDate ? -1 : a.measurementDate > b.measurementDate ? 1 : 0;
    });
    // Phase 38 (D-01/D-14): jeder Ofen bekommt ein loadCalculation-Objekt, auch wenn das Backend
    // keines liefert (Altbestand vor dieser Phase). Ohne dieses Auffuellen zeigt ein solcher Ofen
    // kein Eingabefeld, weil der Formularpfad rotaryKilns.{i}.loadCalculation.<feld> ins Leere
    // liefe (RESEARCH.md Pitfall 5). Alle Felder sind dabei null -- ein leeres Blatt ist ein
    // gueltiger Zustand (D-14), es macht keine Meldung uneinreichbar.
    const loadCalculation = kiln.loadCalculation ?? createEmptyLoadCalculation();
    // Phase 40 (D-09): `calculationBasis: null` bedeutet Konzentration, wie im Rechner
    // (EnvironmentalCalculator) -- das SegmentedControl in 40-07 braucht trotzdem einen
    // gueltigen, nicht-null Formularwert. massFlowCoverage/massFlowOrigin (nur report:read)
    // bleiben unveraendert im Formularstand: 40-07 liest massFlowOrigin von dort, der Server
    // ignoriert beide beim Speichern (belegt in 40-03, T-40-19).
    const normalizedLoadCalculation = {
      ...loadCalculation,
      calculationBasis:
        (loadCalculation as { calculationBasis?: KilnLoadCalculationBasis | null })
          .calculationBasis ?? CALCULATION_BASIS_CONCENTRATION,
    };
    return { ...kiln, measurements, loadCalculation: normalizedLoadCalculation };
  });
}

export function hydrateFormularAB(data: FormularAB): FormularABFormData {
  return {
    badenWuerttemberg: data.badenWuerttemberg,
    bayern: data.bayern,
    berlin: data.berlin,
    brandenburg: data.brandenburg,
    bremen: data.bremen,
    hamburg: data.hamburg,
    hessen: data.hessen,
    mecklenburgVorpommern: data.mecklenburgVorpommern,
    niedersachsen: data.niedersachsen,
    nrw: data.nrw,
    rheinlandPfalz: data.rheinlandPfalz,
    saarland: data.saarland,
    sachsen: data.sachsen,
    sachsenAnhalt: data.sachsenAnhalt,
    schleswigHolstein: data.schleswigHolstein,
    thueringen: data.thueringen,
    notes: data.notes,
  };
}

export function hydrateFormularC(data: FormularC): FormularCFormData {
  return {
    readyMixConcrete: data.readyMixConcrete,
    concreteComponents: data.concreteComponents,
    prefabConcreteComponents: data.prefabConcreteComponents,
    concreteGoods: data.concreteGoods,
    otherSiloCement: data.otherSiloCement,
    plasterAndMortar: data.plasterAndMortar,
    other: data.other,
    baggedCement: data.baggedCement,
    notes: data.notes,
  };
}

export function hydrateFormularD(data: FormularD): FormularDFormData {
  // Gate-Fix 18-08 (Browser-Gate, Punkte 7+10): `{ ...data }` spiegelte die KOMPLETTE Entity in
  // den Form-State -- inklusive configuration/plant/id/status/createdAt. `form.getValues()`
  // patchte diese Metadaten unveraendert zurueck; die verschachtelte `configuration` liess API
  // Platform mit 400 ("Nested documents ... not allowed") ablehnen. FormularD konnte dadurch gar
  // nicht mehr gespeichert werden, sobald der legacy-formData-Zweig (der denselben Bug bislang
  // maskierte) in diesem Plan entfernt wurde.
  //
  // Fix: explizite Whitelist statt Spread+Blacklist. FORMULAR_D_FIELD_KEYS ist aus denselben
  // Katalogen (CEMENT_GROUPS, section2*Fields) kompiliert, die FormularDForm.tsx zum Rendern der
  // 278 Felder nutzt -- kann also nie von der Form abweichen. Ein Blacklist-Ansatz (heutige
  // Metadaten-Keys herausfiltern) waere bei jedem neuen Entity-Feld erneut lautlos zerbrochen.
  //
  // inlandsVersand bleibt bewusst aussen vor: verwaistes Entity-Feld (18-08 Rule-2-Fund), keine
  // UI-Bindung, kein Calculator (D-13), server-autoritativ (report:read-only, FormularD.php).
  const result: FormularDFormData = { notes: data.notes ?? null };
  for (const key of FORMULAR_D_FIELD_KEYS) {
    result[key] = (data[key] as string | null | undefined) ?? null;
  }
  return result;
}

export function hydrateEnvironmentalData(env: EnvironmentalData): EnvironmentalDataFormData {
  return {
    // Production
    clinkerProduction: env.clinkerProduction,
    clinkerReceiptDomestic: env.clinkerReceiptDomestic,
    clinkerReceiptImport: env.clinkerReceiptImport,
    clinkerShipmentDomestic: env.clinkerShipmentDomestic,
    clinkerShipmentExport: env.clinkerShipmentExport,
    clinkerStockJan01: env.clinkerStockJan01,
    clinkerStockDec31: env.clinkerStockDec31,
    clinkerStockChange: env.clinkerStockChange,
    cementProduction: env.cementProduction,
    dustBypass: env.dustBypass,
    dustExhaustFilter: env.dustExhaustFilter,
    electricityConsumptionTotal: env.electricityConsumptionTotal,
    wasteHeatGenerationTotal: env.wasteHeatGenerationTotal,
    averageClinkerContent: env.averageClinkerContent,
    // Fuels and Materials
    primaryFuels: hydratePrimaryFuels(env.primaryFuels),
    // Phase 34: freie Liste statt Katalogpositionen -- keine feste Laenge und keine
    // Katalogreihenfolge mehr, auf die aufzufuellen waere. `?? []` bleibt Pflicht, nicht Kosmetik:
    // form.insertListItem darf nie auf null operieren.
    alternativeFuels: env.alternativeFuels ?? [],
    inputMaterials: hydrateInputMaterials(env.inputMaterials),
    otherInputMaterials: env.otherInputMaterials ?? [],
    // Emission Reduction
    noxSncr: env.noxSncr,
    noxStagedCombustion: env.noxStagedCombustion,
    noxScr: env.noxScr,
    noxOther: env.noxOther,
    noxOtherDescription: env.noxOtherDescription,
    so2Additives: env.so2Additives,
    so2WetScrubber: env.so2WetScrubber,
    so2DryScrubber: env.so2DryScrubber,
    so2Other: env.so2Other,
    so2OtherDescription: env.so2OtherDescription,
    dustReductionBagFilter: env.dustReductionBagFilter,
    dustReductionElectrostaticFilter: env.dustReductionElectrostaticFilter,
    dustReductionHybridFilter: env.dustReductionHybridFilter,
    dustReductionOther: env.dustReductionOther,
    dustReductionOtherDescription: env.dustReductionOtherDescription,
    // Mercury (Hg) reduction
    hgFilterDustDischarge: env.hgFilterDustDischarge,
    hgActivatedCarbon: env.hgActivatedCarbon,
    hgHearthFurnaceCoke: env.hgHearthFurnaceCoke,
    hgOther: env.hgOther,
    hgOtherDescription: env.hgOtherDescription,
    // Quarries
    quarryAreaTotal: env.quarryAreaTotal,
    rehabilitatedAreaPriorYears: env.rehabilitatedAreaPriorYears,
    rehabilitatedAreaCurrentYear: env.rehabilitatedAreaCurrentYear,
    rehabilitatedAreaTotal: env.rehabilitatedAreaTotal,
    rehabilitationNativeForest: env.rehabilitationNativeForest,
    rehabilitationGrassland: env.rehabilitationGrassland,
    rehabilitationWetlands: env.rehabilitationWetlands,
    rehabilitationWaterBodies: env.rehabilitationWaterBodies,
    rehabilitationStreams: env.rehabilitationStreams,
    rehabilitationLakesBiodiversity: env.rehabilitationLakesBiodiversity,
    rehabilitationLakesStorage: env.rehabilitationLakesStorage,
    rehabilitationLakesRecreation: env.rehabilitationLakesRecreation,
    rehabilitationExtensiveAgriculture: env.rehabilitationExtensiveAgriculture,
    rehabilitationConventionalAgriculture: env.rehabilitationConventionalAgriculture,
    rehabilitationCommercialForest: env.rehabilitationCommercialForest,
    rehabilitationParkRecreation: env.rehabilitationParkRecreation,
    rehabilitationCommercialIndustrial: env.rehabilitationCommercialIndustrial,
    rehabilitationOther: env.rehabilitationOther,
    activeQuarryArea: env.activeQuarryArea,
    temporaryHabitat: env.temporaryHabitat,
    // Water
    waterWithdrawal: env.waterWithdrawal,
    waterDischarge: env.waterDischarge,
    waterConsumption: env.waterConsumption,
    waterRecycled: env.waterRecycled,
    // Rohstoffsummen (Phase 31) und clinkerStockChange (Phase 32, P-02): reine Mitfuehrung wie
    // waterConsumption, keine Umformung, kein Standardwert -- serverseitig berechnet,
    // ausschliesslich report:read.
    primaryRawMaterialsClinker: env.primaryRawMaterialsClinker,
    primaryRawMaterialsCementMilling: env.primaryRawMaterialsCementMilling,
    secondaryRawMaterialsClinker: env.secondaryRawMaterialsClinker,
    secondaryRawMaterialsCementMilling: env.secondaryRawMaterialsCementMilling,
    // Die drei abgeleiteten Brennstoff-Zeilenlisten (Phase 30, B-03/B-05/B-09) werden hier bewusst
    // NICHT hydriert: sie sind keine Formularfelder, sondern read-only-Rechenwerte. Die Anzeige
    // speist sich aus der calc-Prop (useReportCalc / initialCalc, siehe Plan 05/06), nicht aus
    // form.getValues() -- anders als waterConsumption, das als Anzeigewert im Form-State mitreist.
    // Rotary Kilns
    rotaryKilns: hydrateRotaryKilns(env.rotaryKilns),
    notes: env.notes,
  };
}

export function hydrateAccidentData(acc: AccidentData): AccidentDataFormData {
  return {
    // General Information
    clinkerProductionDuringPeriod: acc.clinkerProductionDuringPeriod,
    // Employee Counts
    employeesTotal: acc.employeesTotal,
    employeesAdmin: acc.employeesAdmin,
    // Contact Person
    contactName: acc.contactName,
    contactPhone: acc.contactPhone,
    contactEmail: acc.contactEmail,
    // Matrix Row 1 — Hours Worked
    hoursWorkedWage: acc.hoursWorkedWage,
    hoursWorkedAdmin: acc.hoursWorkedAdmin,
    hoursWorkedTemp: acc.hoursWorkedTemp,
    hoursWorkedTotal: acc.hoursWorkedTotal,
    // C-01: der eingefrorene KANUE-Wert reist wie jedes andere Feld im Form-State mit, damit er
    // sowohl die Live-Vorschau (/api/reports/calculate) als auch die Validierung
    // (/api/reports/validate) erreicht -- kein Sonderkanal am geteilten useReportCalc-Hook. Kein
    // Ersatzwert bei null: das Backend faellt dann auf den aktuellen Einstellungswert zurueck.
    annualHoursTemp: acc.annualHoursTemp ?? null,
    // Matrix Row 2 — Accidents with Lost Day
    accidentsLostDayWage: acc.accidentsLostDayWage,
    accidentsLostDayAdmin: acc.accidentsLostDayAdmin,
    accidentsLostDayTemp: acc.accidentsLostDayTemp,
    accidentsLostDayTotal: acc.accidentsLostDayTotal,
    // Matrix Row 3 — Lost Work Days
    lostWorkDaysWage: acc.lostWorkDaysWage,
    lostWorkDaysAdmin: acc.lostWorkDaysAdmin,
    lostWorkDaysTemp: acc.lostWorkDaysTemp,
    lostWorkDaysTotal: acc.lostWorkDaysTotal,
    // MTI own employees
    mtiOwnEmployees: acc.mtiOwnEmployees,
    // Indirect Employees
    indirectHoursWorked: acc.indirectHoursWorked,
    indirectLtis: acc.indirectLtis,
    // Unfalltote
    fatalitiesWage: acc.fatalitiesWage,
    fatalitiesAdmin: acc.fatalitiesAdmin,
    fatalitiesTemp: acc.fatalitiesTemp,
    fatalitiesCommuting: acc.fatalitiesCommuting,
    indirectFatalities: acc.indirectFatalities,
    mtiThirdParties: acc.mtiThirdParties,
    // Comments
    comments: acc.comments,
    notes: acc.notes,
  };
}
